import { useMemo, useState } from 'react';
import {
  createClient,
  normalizePhoneDigits,
  useClients,
  type AcquisitionSource,
} from '@busy/api';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { useT } from '../lib/i18n';
import { useUI } from '../store/ui';
import { BirthdayPicker } from './BirthdayPicker';
import { AcquisitionSourcePicker } from './AcquisitionSourcePicker';

interface NewClientModalProps {
  onClose: () => void;
  /** Optional: when supplied, the duplicate-phone banner offers an
   *  "Open existing" button that closes this modal and asks the host
   *  to focus the existing client. Without it the banner just warns. */
  onOpenExisting?: (clientId: string) => void;
}

export function NewClientModal({ onClose, onOpenExisting }: NewClientModalProps) {
  const toast = useToast();
  const t = useT();
  const clients = useClients();
  const language = useUI((s) => s.language);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  // Birthday pieces — both null = no birthday set. The picker enforces
  // both-or-neither in its own onChange (clearing month also clears day,
  // so this state is never half-populated through normal flow). The
  // `birthdayInvalid` check exists as a belt-and-suspenders guard in
  // case a future flow lets one slip through.
  const [birthdayMonth, setBirthdayMonth] = useState<number | null>(null);
  const [birthdayDay, setBirthdayDay] = useState<number | null>(null);
  const birthdayInvalid =
    (birthdayMonth != null && birthdayDay == null) ||
    (birthdayMonth == null && birthdayDay != null);
  const [notes, setNotes] = useState('');
  // Acquisition source (migration 0075). Optional one-time tag at
  // client creation — "where did this client come from?" Drives the
  // marketing-channel breakdown on the clients tab. Backfillable
  // later from the client detail page.
  const [acquisitionSource, setAcquisitionSource] =
    useState<AcquisitionSource | null>(null);
  const [creating, setCreating] = useState(false);

  // Live duplicate-phone check against the loaded roster — no extra
  // network fetch, just a normalized-digits compare. The DB unique index
  // and createClient's pre-flight check are the source of truth; this
  // banner just surfaces the conflict before the user taps Save.
  const phoneMatch = useMemo(() => {
    const digits = normalizePhoneDigits(phone);
    if (!digits) return null;
    return (clients.data ?? []).find(
      (c) => normalizePhoneDigits(c.phone) === digits,
    ) ?? null;
  }, [phone, clients.data]);

  // Mirrors the cut-timer check-in fallback: when a barber doesn't have
  // time to capture the client's full info, every field can be left
  // blank and we save the row as `Anonymous Client####` so the cut still
  // belongs to a client. Numbering is per-user-roster sequential — bump
  // the highest existing #### by one.
  function nextAnonymousLastName(): string {
    let max = 0;
    for (const c of clients.data ?? []) {
      if (c.first_name !== 'Anonymous') continue;
      const m = (c.last_name ?? '').match(/^Client(\d+)$/);
      if (m) {
        const n = parseInt(m[1] ?? '0', 10);
        if (Number.isFinite(n) && n > max) max = n;
      }
    }
    return `Client${String(max + 1).padStart(4, '0')}`;
  }

  async function handleCreate() {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const useAnonymous = !trimmedFirst && !trimmedLast;
    setCreating(true);
    const { error } = await createClient({
      first_name: useAnonymous ? 'Anonymous' : trimmedFirst,
      last_name: useAnonymous ? nextAnonymousLastName() : trimmedLast,
      phone: phone.trim() || undefined,
      notes: notes.trim() || undefined,
      birthday_month: birthdayMonth,
      birthday_day: birthdayDay,
      acquisition_source: acquisitionSource,
    });
    setCreating(false);
    if (error) {
      toast(t('newClient.createError', { message: error.message }), 'error');
    } else {
      toast(t('newClient.added'), 'success');
      onClose();
    }
  }

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={t('newClient.aria')}>
      <ModalCloseButton onClose={onClose} />
      <div className="border-b border-line p-4">
        <h2 className="font-display text-xl">{t('newClient.title')}</h2>
      </div>
      <div className="p-4 space-y-4">
        <Input
          label={t('newClient.field.firstName')}
          value={firstName}
          onChange={setFirstName}
          autoFocus
        />
        <Input label={t('newClient.field.lastName')} value={lastName} onChange={setLastName} />
        <Input
          label={t('newClient.field.phone')}
          type="tel"
          value={phone}
          onChange={setPhone}
          placeholder="(555) 123-4567"
        />
        <div>
          <label className="block text-xs font-semibold text-ink-500 mb-1">
            {t('newClient.field.birthday')}
          </label>
          <BirthdayPicker
            month={birthdayMonth}
            day={birthdayDay}
            onChange={(m, d) => {
              setBirthdayMonth(m);
              setBirthdayDay(d);
            }}
            language={language}
          />
          {birthdayInvalid ? (
            <p className="text-[11px] text-red-600 mt-1">
              {t('newClient.birthday.invalid')}
            </p>
          ) : (
            <p className="text-[11px] text-ink-500 mt-1">
              {t('newClient.birthday.help')}
            </p>
          )}
        </div>
        {phoneMatch && (
          <div className="rounded-lg border border-amber-500/60 bg-amber-50 dark:bg-amber-500/10 px-3 py-2.5 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <strong className="block text-sm text-ink-900 dark:text-ink-100 truncate">
                {t('newClient.dupPhone.title', {
                  name:
                    `${phoneMatch.first_name ?? ''} ${phoneMatch.last_name ?? ''}`.trim() ||
                    t('today.unnamed'),
                })}
              </strong>
              <span className="text-xs text-ink-700 dark:text-ink-300">
                {t('newClient.dupPhone.body')}
              </span>
            </div>
            {onOpenExisting && (
              <button
                type="button"
                onClick={() => {
                  onOpenExisting(phoneMatch.id);
                  onClose();
                }}
                className="shrink-0 h-9 px-3 rounded-lg bg-amber-600 text-white font-bold text-xs"
              >
                {t('newClient.dupPhone.open')}
              </button>
            )}
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-ink-500 mb-1">
            {t('newClient.field.notes')}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={t('newClient.notesPlaceholder')}
            className="w-full px-3 py-2 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500 resize-none"
          />
        </div>
        {/* How'd they find you? Marketing-channel tag captured once at
            client creation. Optional — skipping is fine; this is a
            "nice to know" not "required for save." Backfillable later
            from the client detail page. */}
        <div>
          <label className="block text-xs font-semibold text-ink-500 mb-1">
            {t('newClient.field.acquisitionSource')}
          </label>
          <AcquisitionSourcePicker
            value={acquisitionSource}
            onChange={setAcquisitionSource}
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating || !!phoneMatch || birthdayInvalid}
            className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
          >
            {creating ? t('newClient.adding') : t('newClient.add')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-6 h-11 rounded-lg border border-line bg-surface font-semibold"
          >
            {t('newClient.cancel')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
  required,
  placeholder,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-ink-500 mb-1">
        {label}
        {required && <span className="text-red-600 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className="w-full h-10 px-3 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
      />
    </div>
  );
}
